import { AxiosRequestConfig } from 'axios';
import { getApiInstance } from 'services/Api';

type SerializedDocument = {
	serializedData: string;
	blobData: Blob;
	displayAsPdf: boolean;
	contentType: string;
} | null;

export const previewDocumentById = async (
	documentId: string,
	date: string,
	title: string
): Promise<SerializedDocument> => {
	try {
		const apiInstance = await getApiInstance('/commercial-selfservice/');

		const response = await apiInstance?.documentApiServiceLayer?.fetchCustomerDocument(documentId, requestHeaders);

		const data = response.data as Blob;
		const contentType = data.type;

		return serializeDocumentType(contentType, data, title, date);
	} catch (error) {
		console.error('fetchCustomerDocument: Error fetching document', error);
		throw error;
	}
};

export const previewPolicyById = async (policyNumber: string): Promise<SerializedDocument> => {
	try {
		const apiInstance = await getApiInstance('/commercial-selfservice/');
		const response = await apiInstance.documentApiServiceLayer.fetchPolicyDocument(policyNumber, requestHeaders);

		const data = response.data as Blob;
		const contentType = data.type;

		return serializeDocumentType(contentType, data, policyNumber);
	} catch (error) {
		console.error('fetchCustomerDocument: Error fetching document', error);
		throw error;
	}
};

const serializeDocumentType = async (contentType: string, data: BlobPart, title: string, date?: string) => {
	switch (contentType) {
		case 'application/xml': {
			const blob = new Blob([data], { type: 'application/xml' });
			return { serializedData: await xmlBlobToHtmlView(blob), blobData: blob, displayAsPdf: false, contentType };
		}

		case 'text/plain': {
			const blob = new Blob([data], { type: 'text/plain' });
			return {
				serializedData: await plainBlobToHtmlView(blob),
				blobData: blob,
				displayAsPdf: false,
				contentType,
			};
		}

		case 'application/pdf': {
			const blob = new Blob([data], { type: 'application/pdf' });
			const pdfUrl = window.URL.createObjectURL(blob);
			return { serializedData: pdfUrl, blobData: blob, displayAsPdf: true, contentType };
		}

		default: {
			const fileExt = getFileExtension(contentType);
			const blob = new Blob([data], { type: contentType });

			downloadBlob(blob, title, fileExt, date);
			return null; // or an appropriate value to indicate no further processing
		}
	}
};

const xmlBlobToHtmlView = (blob: Blob): Promise<string | null> => {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		const parser = new DOMParser();

		reader.addEventListener('loadend', (event) => {
			const text = event.target?.result as string;
			const xmlDoc = parser.parseFromString(text, 'text/xml');

			const elements = xmlDoc.getElementsByTagName('wcm:element');
			let contentNode = null;

			for (let i = 0; i < elements.length; i++) {
				if (elements[i].getAttribute('name') === 'Content') {
					contentNode = elements[i];
					break;
				}
			}

			if (contentNode) {
				const htmlContent = contentNode.textContent || '';
				resolve(htmlContent);
			} else {
				console.warn('No content found in the XML document.');
				resolve(null);
			}
		});

		reader.onerror = (error) => {
			reject(error);
		};

		reader.readAsText(blob);
	});
};

const plainBlobToHtmlView = (blob: Blob): Promise<string | null> => {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();

		reader.addEventListener('loadend', (event) => {
			const textContent = event.target?.result as string;
			if (textContent) resolve(textContent);
			else reject();
		});
		reader.readAsText(blob);
	});
};

export const getFileExtension = (contentType: string): string => {
	const parts = contentType.split('/');
	return parts.length > 1 ? parts[1] : 'bin';
};

export const formatDate = (dateString?: string): string => {
	if (!dateString) return 'unknown_date';
	const date = new Date(dateString);
	return `${date.getDate()}-${date.getMonth() + 1}-${date.getFullYear()}`;
};

const requestHeaders: AxiosRequestConfig = {
	headers: {
		'Content-Type': 'application/json',
	},
	responseType: 'blob',
};

export const downloadBlob = (data: Blob, name: string, fileExt: string, date?: string) => {
	const url = window.URL.createObjectURL(data);
	const link = document.createElement('a');
	link.href = url;
	link.setAttribute('download', `${name}${date ? '_' + date : ''}.${fileExt}`);
	document.body.appendChild(link);
	link.click();
	link.parentNode?.removeChild(link);
	window.URL.revokeObjectURL(url);
};
